/**
 * FeaturePool - Left panel of the sidebar builder
 *
 * Shows features NOT assigned to any category (the "pool").
 * Features can be dragged from here into the canvas categories.
 * Grouped by original nav_group for discoverability.
 */

import React, { useState, useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Search, GripVertical, Box } from 'lucide-react';
import { MODULE_ICONS } from '../../../constants/profile_constants.js';

function DraggableFeature({ feature, allFeatures }) {
  const featureData = allFeatures.find(f => f.id === feature) || { id: feature, label: feature };
  const Icon = MODULE_ICONS[feature] || Box;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pool-${feature}`,
    data: {
      type: 'pool-item',
      featureId: feature,
      fromContainer: 'pool',
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing transition-all text-sm
        ${isDragging
          ? 'opacity-40 bg-blue-500/20 ring-1 ring-blue-500/40'
          : 'bg-white dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-600/50 border border-gray-200 dark:border-slate-600'
        }`}
    >
      <GripVertical size={12} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
      <Icon size={14} className="text-gray-500 dark:text-slate-400 flex-shrink-0" />
      <span className="text-gray-700 dark:text-slate-300 truncate text-xs">
        {featureData.label}
      </span>
      {featureData.adminOnly && (
        <span className="ml-auto text-[9px] px-1 py-0.5 bg-amber-500/20 text-amber-500 rounded flex-shrink-0">
          Admin
        </span>
      )}
    </div>
  );
}

export function FeaturePool({ poolItems, allFeatures, groups }) {
  const [search, setSearch] = useState('');

  // Group pool items by their original nav_group
  const grouped = useMemo(() => {
    const result = {};
    const filtered = search
      ? poolItems.filter(id => {
          const f = allFeatures.find(x => x.id === id);
          return f && (
            f.label.toLowerCase().includes(search.toLowerCase()) ||
            f.id.toLowerCase().includes(search.toLowerCase())
          );
        })
      : poolItems;

    filtered.forEach(id => {
      const f = allFeatures.find(x => x.id === id);
      const group = f?.group || 'other';
      if (!result[group]) result[group] = [];
      result[group].push(id);
    });
    return result;
  }, [poolItems, allFeatures, search]);

  const totalFiltered = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
          Fonctionnalités disponibles
        </h3>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filtrer..."
            className="w-full pl-8 pr-3 py-1.5 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-1.5">
          {totalFiltered} disponible{totalFiltered !== 1 ? 's' : ''} — glisser vers le canevas
        </div>
      </div>

      {/* Pool content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {totalFiltered === 0 ? (
          <div className="text-center text-xs text-gray-400 dark:text-slate-500 py-8">
            {search ? 'Aucun résultat' : 'Toutes les fonctionnalités sont assignées'}
          </div>
        ) : (
          Object.entries(grouped)
            .sort(([a], [b]) => {
              const orderA = groups[a]?.order || 99;
              const orderB = groups[b]?.order || 99;
              return orderA - orderB;
            })
            .map(([groupCode, items]) => (
              <div key={groupCode}>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-1 px-1">
                  {groups[groupCode]?.label || groupCode}
                </div>
                <div className="space-y-1">
                  {items.map(id => (
                    <DraggableFeature
                      key={id}
                      feature={id}
                      allFeatures={allFeatures}
                    />
                  ))}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
}

export default FeaturePool;
